import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useState } from "react";

import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { RelatedRecord, Tenant } from "../../../types/GlobalInterfaces";
import { currentCloudDataAtom } from "../../../atoms/GlobalAtoms";
import { FileInterface } from "../../../types/RecordInterfaces";
import { newToastEvent } from "../../../utils/ToastUtil";
import API from "../../api/API";

type AllowableCloudUpdateKeys =
  | "BillingAddress"
  | "BillingContactName"
  | "BillingContactEmail"
  | "CompanyName"
  | "CompanyWebsite"
  | "Tier"
  | "Name"
  | "RelatedRecords"
  | "Global"
  | "BrieDisabled"
  | "Logo"
  | "ColorTheme"
  | "HiddenAIModels";

interface MutationProps {
  tenantID: string;
  payload: Partial<{
    [key in AllowableCloudUpdateKeys]:
      | string
      | string[]
      | boolean
      | RelatedRecord[]
      | FileInterface
      | null;
  }>;
  onSuccess?: () => void;
}

// For updating a basic information about a cloud in the supportal
const useUpdateCloud = () => {
  const [processing, setProcessing] = useState(false);
  const setCurrentCloudData = useSetAtom(currentCloudDataAtom);
  const queryClient = useQueryClient();

  const updateCloud = useMutation({
    mutationFn: ({ tenantID, payload }: MutationProps) => {
      setProcessing(true);
      return API.updateCloud(tenantID, payload);
    },
    onSuccess: async (data, { tenantID, onSuccess }) => {
      const newTenant = data.data.data.tenant;

      setCurrentCloudData({
        Global: !!newTenant?.Global,
        BrieDisabled: !!newTenant?.BrieDisabled,
        RelatedRecords: newTenant?.RelatedRecords ?? [],
        ColorTheme: newTenant?.ColorTheme,
        Logo: newTenant?.Logo,
      });
      await queryClient.invalidateQueries({
        queryKey: ["cloud", tenantID],
      });

      setProcessing(false);

      queryClient.setQueryData<Tenant[]>(["allClouds"], (prev) => {
        if (!Array.isArray(prev)) return prev;

        return prev.map((c) => (c.id === tenantID ? newTenant : c));
      });

      newToastEvent("success", "Successfully updated cloud");
      callFunctionIfExists(onSuccess);
    },
    onError: (err) => {
      newToastEvent(
        "error",
        `Error updating cloud! Error: ${getErrorMessage(err)}`
      );
      setProcessing(false);
    },
  });

  return {
    updateCloud: updateCloud.mutate,
    updatingCloud: updateCloud.isPending || processing,
    updateCloudError: updateCloud.error
      ? getErrorMessage(updateCloud.error)
      : null,
  };
};

export default useUpdateCloud;
